import { z } from "zod";
import {
  ArtifactKind,
  ArtifactStatus,
  AssetKind,
  AssetStatus,
  CreditSource,
  HoldStatus,
  JobPhase,
  JobStatus,
  LedgerType,
  TrashOrigin,
} from "./domain";
import { ExecutedPipelineSpec, PipelineSpec } from "./pipeline";
import { PriceBook } from "./pricing";
import { PromoEffects } from "./promo";

// §9 Zeilen-Schemas — Parse-Boundary an der Worker↔DB-Grenze. ──────────────────
// WICHTIG (Hyperdrive-Realität): der Worker fährt postgres.js mit `prepare:false` +
// `fetch_types:false` (Pooler-/Edge-kompatibel, db.ts). Damit findet KEIN clientseitiges
// Type-Parsing statt → timestamptz kommt als roher Postgres-Text
// (`YYYY-MM-DD HH:MM:SS[.ffffff]±TZ`, NICHT ISO mit `T`), jsonb als roher JSON-Text. Die
// Schemas akzeptieren daher beide Welten (geparst lokal/Tests UND roh über Hyperdrive).

const Uuid = z.string().uuid();
/** timestamptz: Date (lokal/Test, falls Parsing aktiv), ISO-8601 mit Offset (JSON-Pfad) ODER
 *  Postgres-Text `YYYY-MM-DD HH:MM:SS[.ffffff]±TZ` (Hyperdrive, fetch_types:false). Alle drei
 *  sind `new Date(...)`-fähig. */
const Ts = z.union([
  z.string().datetime({ offset: true }),
  z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?[+-]\d{2}(:\d{2}(:\d{2})?)?$/),
  z.date(),
]);
/** jsonb über Hyperdrive kann je nach Transport als GEPARSTES Objekt (postgres.js mit bekanntem
 *  OID) ODER als roher JSON-TEXT (kein clientseitiges Parsing) ankommen. Diese Coercion
 *  vereinheitlicht beide Welten: Strings werden vor der Schema-Validierung per JSON.parse
 *  geparst, alles andere unverändert durchgereicht (schlägt der Parse fehl, scheitert das
 *  Zielschema definiert). SCHREIBEND gilt das Gegenstück: rohe Objekte/Arrays an `::jsonb`
 *  geben — NIE JSON.stringify (postgres.js doppelkodiert das sonst zu einem jsonb-Skalar). */
export function coerceJsonb(v: unknown): unknown {
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
}
/** Typisierte jsonb-Spalte: Hyperdrive-Coercion + KONKRETES Zielschema (PriceBook/PipelineSpec/
 *  PromoEffects/…). Pendant zu `Json` für beliebige Objekte; deckt den jsonb-LESE-Pfad ab. */
export function Jsonb<T extends z.ZodTypeAny>(schema: T): z.ZodEffects<T, z.output<T>, unknown> {
  return z.preprocess(coerceJsonb, schema);
}
/** jsonb als beliebiges Objekt (metadata/settings) — robuste Parse-Boundary. */
const Json = Jsonb(z.record(z.string(), z.unknown()));
/** bigint-sicher: Treiber liefern int8 je nach Konfig als bigint, string oder number. */
const BigIntish = z.union([z.bigint(), z.string(), z.number()]);

// §9.2 profiles
export const ProfileRow = z.object({
  id: Uuid,
  display_name: z.string().nullable(),
  created_at: Ts,
});
export type ProfileRow = z.infer<typeof ProfileRow>;

// §9.3 folders
export const FolderRow = z.object({
  id: Uuid,
  user_id: Uuid,
  parent_id: Uuid.nullable(),
  name: z.string(),
  tool_id: z.string().nullable(), // D143: gesetzt ⇒ tool-spezifischer System-Default-Ordner; null = normaler Ordner
  trashed_at: Ts.nullable(),
  trashed_via: TrashOrigin.nullable(),
  created_at: Ts,
  updated_at: Ts,
});
export type FolderRow = z.infer<typeof FolderRow>;

// §9.4 assets
export const AssetRow = z.object({
  id: Uuid,
  user_id: Uuid,
  folder_id: Uuid.nullable(),
  parent_asset_id: Uuid.nullable(),
  kind: AssetKind,
  status: AssetStatus,
  trashed_at: Ts.nullable(),
  trashed_via: TrashOrigin.nullable(),
  r2_key: z.string().nullable(),
  source_job_id: Uuid.nullable(),
  not_live_since: Ts.nullable(),
  metadata: Json,
  created_at: Ts,
  updated_at: Ts,
});
export type AssetRow = z.infer<typeof AssetRow>;

// §9.5 jobs
export const JobRow = z.object({
  id: Uuid,
  user_id: Uuid,
  tool_id: z.string(),
  settings: Json,
  planned_pipeline_spec: Jsonb(PipelineSpec),
  // nullable NACH INNEN: coerceJsonb läuft VOR der Nullable-Prüfung, damit auch roher jsonb-Text
  // "null" (nicht nur SQL-NULL) sauber zu null wird (sonst feuert das innere Nicht-Null-Schema).
  executed_pipeline_spec: Jsonb(ExecutedPipelineSpec.nullable()),
  status: JobStatus,
  phase: JobPhase.nullable(),
  target_folder_id: Uuid.nullable(),
  price_book_version: z.number().int(),
  estimated_credits: z.number().int(),
  charged_credits: z.number().int(),
  not_live_since: Ts.nullable(),
  error: z.string().nullable(),
  created_at: Ts,
  started_at: Ts.nullable(),
  finished_at: Ts.nullable(),
  updated_at: Ts,
});
export type JobRow = z.infer<typeof JobRow>;

// §9.6 job_inputs
export const JobInputRow = z.object({
  job_id: Uuid,
  asset_id: Uuid,
  role: z.string(),
});
export type JobInputRow = z.infer<typeof JobInputRow>;

// §9.6 job_artifacts
export const JobArtifactRow = z.object({
  id: Uuid,
  job_id: Uuid,
  node_id: z.string(),
  port: z.string(),
  ordinal: z.number().int(),
  artifact_kind: ArtifactKind,
  r2_key: z.string().nullable(),
  checksum: z.string().nullable(),
  size_bytes: BigIntish.nullable(),
  status: ArtifactStatus,
  visible_asset_id: Uuid.nullable(),
  created_at: Ts,
});
export type JobArtifactRow = z.infer<typeof JobArtifactRow>;

// §9.7 price_books — payload ist die §3.6-DSL, nicht beliebiges JSON
export const PriceBookRow = z.object({
  version: z.number().int(),
  payload: Jsonb(PriceBook),
  active: z.boolean(),
  created_at: Ts,
});
export type PriceBookRow = z.infer<typeof PriceBookRow>;

// §9.8 credit_lots
export const CreditLotRow = z.object({
  id: Uuid,
  user_id: Uuid,
  source: CreditSource,
  amount_granted: z.number().int(),
  amount_remaining: z.number().int(),
  granted_at: Ts,
  expires_at: Ts.nullable(),
  subscription_id: Uuid.nullable(),
  billing_event_id: z.string().nullable(),
});
export type CreditLotRow = z.infer<typeof CreditLotRow>;

// §9.8 credit_holds
export const CreditHoldRow = z.object({
  id: Uuid,
  user_id: Uuid,
  job_id: Uuid.nullable(),
  amount_held: z.number().int(),
  amount_settled: z.number().int(),
  status: HoldStatus,
  created_at: Ts,
  expires_at: Ts.nullable(),
});
export type CreditHoldRow = z.infer<typeof CreditHoldRow>;

// §9.8 credit_transactions (Audit-Trail)
export const CreditTransactionRow = z.object({
  id: Uuid,
  user_id: Uuid,
  type: LedgerType,
  amount: z.number().int(),
  lot_id: Uuid.nullable(),
  hold_id: Uuid.nullable(),
  job_id: Uuid.nullable(),
  phase: JobPhase.nullable(),
  idempotency_key: z.string().nullable(),
  created_at: Ts,
});
export type CreditTransactionRow = z.infer<typeof CreditTransactionRow>;

// §9.8 credit_hold_lots — Lot-Pinning je Hold (Join-RLS via user_id, D44)
export const CreditHoldLotRow = z.object({
  hold_id: Uuid,
  lot_id: Uuid,
  user_id: Uuid,
  amount_reserved: z.number().int(),
  amount_settled: z.number().int(),
  amount_released: z.number().int(),
  amount_expired: z.number().int(),
});
export type CreditHoldLotRow = z.infer<typeof CreditHoldLotRow>;

// §9.8 credit_hold_items — Worst-Case-Allokation je node/phase (D36).
// user_id explizit mitgeführt für Join-RLS (PRD §9.1 nennt credit_hold_items ausdrücklich).
export const CreditHoldItemRow = z.object({
  hold_id: Uuid,
  job_id: Uuid,
  user_id: Uuid,
  node_id: z.string(),
  phase: JobPhase,
  amount_reserved: z.number().int(),
  amount_settled: z.number().int(),
  amount_released: z.number().int(),
});
export type CreditHoldItemRow = z.infer<typeof CreditHoldItemRow>;

// §9.8 credit_adjustments — Negativ-Saldo / Clawback (D37)
export const CreditAdjustmentRow = z.object({
  id: Uuid,
  user_id: Uuid,
  amount: z.number().int(),
  reason: z.string(),
  billing_event_id: z.string().nullable(),
  created_at: Ts,
});
export type CreditAdjustmentRow = z.infer<typeof CreditAdjustmentRow>;

// §9.8 credit_adjustment_lots — partielle Clawback-Zuordnung (D44)
export const CreditAdjustmentLotRow = z.object({
  adjustment_id: Uuid,
  lot_id: Uuid,
  amount: z.number().int(),
});
export type CreditAdjustmentLotRow = z.infer<typeof CreditAdjustmentLotRow>;

// §9.9 subscriptions
export const SubscriptionRow = z.object({
  id: Uuid,
  user_id: Uuid,
  plan_id: z.string(),
  status: z.string(),
  monthly_credits: z.number().int(),
  current_period_start: Ts,
  current_period_end: Ts,
  provider: z.string().nullable(),
  provider_subscription_id: z.string().nullable(),
  last_event_seq: BigIntish.nullable(),
  created_at: Ts,
  updated_at: Ts,
});
export type SubscriptionRow = z.infer<typeof SubscriptionRow>;

// §9.14 billing_events
export const BillingEventRow = z.object({
  provider: z.string(),
  provider_event_id: z.string(),
  type: z.string(),
  event_seq: BigIntish.nullable(),
  processed_at: Ts,
});
export type BillingEventRow = z.infer<typeof BillingEventRow>;

// §9.14 billing_products
export const BillingProductRow = z.object({
  id: Uuid,
  paddle_price_id: z.string(),
  kind: z.string(),
  credits: z.number().int(),
  validity_days: z.number().int().nullable(),
  active: z.boolean(),
  display_name: z.string().nullable(),
  created_at: Ts,
});
export type BillingProductRow = z.infer<typeof BillingProductRow>;

// §9.15 promo_codes — effects ist die validierte Publish-Gate-DSL (P1-3), nicht beliebiges JSON
export const PromoCodeRow = z.object({
  code: z.string(),
  label: z.string().nullable(),
  effects: Jsonb(PromoEffects),
  paddle_discount_id: z.string().nullable(),
  active: z.boolean(),
  valid_from: Ts.nullable(),
  valid_until: Ts.nullable(),
  max_users: z.number().int().nullable(),
  per_user_limit: z.number().int().nullable(),
  redeemed_count: z.number().int(),
  created_at: Ts,
  updated_at: Ts,
});
export type PromoCodeRow = z.infer<typeof PromoCodeRow>;

// §9.15 code_users — Distinct-User-Cap + Grant-Sperre
export const CodeUserRow = z.object({
  code: z.string(),
  user_id: Uuid,
  first_used_at: Ts,
  use_count: z.number().int(),
  grant_claimed: z.boolean(),
});
export type CodeUserRow = z.infer<typeof CodeUserRow>;

// §9.15 code_redemptions — Audit je Effekt/Event (inkl. '__event__'-Marker + Reservierung)
export const CodeRedemptionRow = z.object({
  id: Uuid,
  code: z.string(),
  user_id: Uuid,
  effect_id: z.string(),
  effect_type: z.string(),
  billing_event_id: z.string().nullable(),
  idempotency_key: z.string().nullable(),
  granted_lot_id: Uuid.nullable(),
  reserved_until: Ts.nullable(),
  applied_at: Ts,
});
export type CodeRedemptionRow = z.infer<typeof CodeRedemptionRow>;
